import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useObraDetalle } from '@/hooks/useObraDetalle';
import { formatearCRC } from '@adelante/shared';
import type {
  CuentaEnObra,
  KpiObra,
  ObraDetalle,
  PartidaEnObra,
  ProductoEnObra,
  PuntoAvanceMensual,
} from '@adelante/shared';
import { CircleNotch, X } from '@phosphor-icons/react';
import { useEffect } from 'react';

const NOMBRES_MES = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

/**
 * Drawer lateral derecho con el detalle completo de una obra.
 *
 * Se abre cuando `worksNo` es no-null. Inspirado en la app de ventas
 * de Adelante (panel flotante sobre el contenido). UX:
 * - Slide-in desde la derecha
 * - Click en backdrop oscuro o tecla ESC para cerrar
 * - Body scroll bloqueado mientras está abierto
 * - Width responsive: ~640px en desktop, full en mobile
 *
 * Contenido (carga desde /api/obra-detalle?worksNo=X):
 * - Cabecera (nombre, status, área, manager, fechas)
 * - KPIs grandes (Avance, Costo, Presupuesto, CPI, Sobrecosto)
 * - Sparkline de avance histórico mensual
 * - Tabla de partidas (task_no) con split directo/indirecto
 * - Top 30 productos consumidos
 * - Top 30 cuentas movidas
 */
export function DrawerDetalleObra({
  worksNo,
  onClose,
}: {
  worksNo: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useObraDetalle(worksNo);

  useEffect(() => {
    if (!worksNo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [worksNo, onClose]);

  if (!worksNo) return null;

  return (
    <>
      {/* Backdrop decorativo (aria-hidden); el cierre por teclado es vía ESC y el botón X. */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop decorativo, cierre accesible por ESC y botón Cerrar */}
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} aria-hidden="true" />
      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[680px] flex-col bg-zinc-50 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-5 py-3">
          <div>
            <div className="text-xs text-muted-foreground">Detalle de Obra</div>
            <div className="text-lg font-semibold tabular-nums">{worksNo}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-2 text-muted-foreground hover:bg-zinc-100"
            aria-label="Cerrar"
            title="Cerrar (ESC)"
          >
            <X className="h-5 w-5" weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
              Cargando detalle...
            </div>
          )}

          {error && (
            <Card className="border-destructive">
              <CardContent className="pt-6 text-sm text-destructive">
                Error: {error.message}
              </CardContent>
            </Card>
          )}

          {data?.data && <ContenidoDetalle detalle={data.data} />}
        </div>
      </div>
    </>
  );
}

function ContenidoDetalle({ detalle }: { detalle: ObraDetalle }) {
  const { cabecera, kpi, avanceHistorico, productos, cuentas, partidas } = detalle;

  return (
    <>
      {/* Cabecera */}
      <Card>
        <CardContent className="pt-4 space-y-2">
          <div className="text-base font-medium">{cabecera.displayName ?? cabecera.worksNo}</div>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            {cabecera.areaCosteo && (
              <Badge variant="secondary" className="text-[10px]">
                {cabecera.areaCosteo}
              </Badge>
            )}
            {cabecera.status && (
              <Badge variant="outline" className="text-[10px]">
                {cabecera.status}
              </Badge>
            )}
            {cabecera.areaProrrateadaM2 && (
              <span>· {cabecera.areaProrrateadaM2.toFixed(1)} m²</span>
            )}
            {cabecera.projectManager && <span>· PM: {cabecera.projectManager}</span>}
            {cabecera.startingDate && <span>· Inicio: {cabecera.startingDate}</span>}
            {cabecera.endingDate && <span>· Fin: {cabecera.endingDate}</span>}
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      {kpi && <KpisGrid kpi={kpi} />}

      {/* Avance histórico */}
      {avanceHistorico.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Avance acumulado mensual</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <Sparkline puntos={avanceHistorico} />
            <TablaAvance puntos={avanceHistorico} />
          </CardContent>
        </Card>
      )}

      {/* Partidas SEPARADAS por Directo vs Indirecto.
          Convención BC Adelante: las partidas indirectas empiezan con "CI"
          (CI.PV Postventa, CI.SC Costos Servicios Centrales, etc.).
          Adicionalmente usamos el flag es_costo_directo del fact si está disponible.
          fact_costos viene de ledger entries (nivel "Auxiliar" en BC), nunca
          incluye agrupadores Total → no hay double-counting. */}
      {partidas.length > 0 && <SeccionPartidas partidas={partidas} />}

      {/* Top productos */}
      {productos.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Top productos (top 30 por costo)</CardTitle>
          </CardHeader>
          <CardContent className="pt-2 p-0">
            <table className="w-full text-xs">
              <thead className="border-b">
                <tr className="text-left text-muted-foreground">
                  <th className="px-3 py-1.5">Código</th>
                  <th className="px-3 py-1.5">Descripción</th>
                  <th className="px-3 py-1.5 text-right">Cant</th>
                  <th className="px-3 py-1.5">UM</th>
                  <th className="px-3 py-1.5 text-right">Costo</th>
                </tr>
              </thead>
              <tbody>
                {productos.map((p: ProductoEnObra) => (
                  <tr
                    key={p.llaveCodigo ?? p.consolidatedCode ?? Math.random()}
                    className="border-b border-zinc-100"
                  >
                    <td className="px-3 py-1.5 font-mono text-[10px]">
                      {p.consolidatedCode ?? '—'}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="truncate max-w-[200px]">{p.description ?? '—'}</div>
                      {p.itemCategoryCode && (
                        <div className="text-[10px] text-muted-foreground">
                          {p.itemCategoryCode}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {p.totalQuantity.toLocaleString('es-CR', { maximumFractionDigits: 1 })}
                    </td>
                    <td className="px-3 py-1.5 text-[10px]">{p.unidadMedida ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {formatearCRC(p.totalCostoLcy)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Top cuentas */}
      {cuentas.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Top cuentas con movimientos</CardTitle>
          </CardHeader>
          <CardContent className="pt-2 p-0">
            <table className="w-full text-xs">
              <thead className="border-b">
                <tr className="text-left text-muted-foreground">
                  <th className="px-3 py-1.5">Cuenta</th>
                  <th className="px-3 py-1.5">Descripción</th>
                  <th className="px-3 py-1.5 text-right">Mov.</th>
                  <th className="px-3 py-1.5 text-right">Costo</th>
                </tr>
              </thead>
              <tbody>
                {cuentas.map((c: CuentaEnObra) => (
                  <tr key={c.llaveCodigo ?? Math.random()} className="border-b border-zinc-100">
                    <td className="px-3 py-1.5 font-mono text-[10px]">{c.codigo ?? '—'}</td>
                    <td className="px-3 py-1.5">
                      <div className="truncate max-w-[240px]">{c.description ?? '—'}</div>
                    </td>
                    <td className="px-3 py-1.5 text-right text-[10px] text-muted-foreground">
                      {c.rowCount}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {formatearCRC(c.totalCostoLcy)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {!kpi && avanceHistorico.length === 0 && productos.length === 0 && (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Sin datos de KPIs, avance ni costos para esta obra.
          </CardContent>
        </Card>
      )}
    </>
  );
}

/**
 * Renderiza las partidas separadas en 2 secciones:
 *   - Directas (task_no NO empieza con "CI")
 *   - Indirectas (task_no empieza con "CI")
 *
 * Cada sección tiene su propio header con subtotal. Adicionalmente
 * mostramos el subtotal Directo / Indirecto / General arriba.
 */
function SeccionPartidas({ partidas }: { partidas: PartidaEnObra[] }) {
  const esIndirecta = (p: PartidaEnObra) => {
    // Doble criterio: prefix "CI" en task_no OR es_costo_directo=false (mayoría
    // de las filas tienen ambos consistentes, pero por defensa usamos ambos)
    if (p.taskNo?.toUpperCase().startsWith('CI')) return true;
    // Si la partida tiene SOLO costoIndirecto y nada directo
    if (p.costoIndirecto > 0 && p.costoDirecto === 0) return true;
    return false;
  };

  const directas = partidas.filter((p) => !esIndirecta(p));
  const indirectas = partidas.filter((p) => esIndirecta(p));

  const totalDirecto = directas.reduce((acc, p) => acc + p.totalCostoLcy, 0);
  const totalIndirecto = indirectas.reduce((acc, p) => acc + p.totalCostoLcy, 0);
  const totalGeneral = totalDirecto + totalIndirecto;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Partidas — Costo directo vs indirecto</CardTitle>
      </CardHeader>
      <CardContent className="pt-2 px-4 pb-4 space-y-4">
        {/* Subtotales arriba */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded border border-zinc-200 bg-zinc-50 p-2">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Directo</div>
            <div className="text-sm font-semibold tabular-nums">{formatearCRC(totalDirecto)}</div>
          </div>
          <div className="rounded border border-zinc-200 bg-zinc-50 p-2">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">
              Indirecto
            </div>
            <div className="text-sm font-semibold tabular-nums text-amber-700">
              {formatearCRC(totalIndirecto)}
            </div>
          </div>
          <div className="rounded border border-zinc-300 bg-zinc-100 p-2">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Total</div>
            <div className="text-sm font-bold tabular-nums">{formatearCRC(totalGeneral)}</div>
          </div>
        </div>

        {/* Sección Directas */}
        {directas.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-zinc-700 mb-1.5 px-1">
              Costo Directo · {directas.length} partidas
            </div>
            <TablaPartidas partidas={directas} acento="default" />
          </div>
        )}

        {/* Sección Indirectas */}
        {indirectas.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-amber-700 mb-1.5 px-1">
              Costo Indirecto · {indirectas.length} partidas
            </div>
            <TablaPartidas partidas={indirectas} acento="indirecto" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TablaPartidas({
  partidas,
  acento,
}: {
  partidas: PartidaEnObra[];
  acento: 'default' | 'indirecto';
}) {
  const bgHeader = acento === 'indirecto' ? 'bg-amber-50' : 'bg-zinc-50';
  return (
    <div className="overflow-x-auto rounded border border-zinc-200">
      <table className="w-full text-xs">
        <thead className={`${bgHeader} border-b`}>
          <tr className="text-left text-muted-foreground">
            <th className="px-3 py-1.5">Partida</th>
            <th className="px-3 py-1.5 text-right w-24">Cant</th>
            <th className="px-3 py-1.5 text-right w-32">Costo</th>
          </tr>
        </thead>
        <tbody>
          {partidas.map((p: PartidaEnObra, idx: number) => (
            <tr key={`${p.taskNo ?? 'sin'}-${idx}`} className="border-b border-zinc-100">
              <td className="px-3 py-1.5">
                <div className="font-medium">{p.taskNo ?? '(sin)'}</div>
                {p.tareaDisplay && (
                  <div className="text-[10px] text-muted-foreground truncate max-w-[280px]">
                    {p.tareaDisplay}
                  </div>
                )}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                {p.totalQuantity
                  ? p.totalQuantity.toLocaleString('es-CR', { maximumFractionDigits: 1 })
                  : '—'}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                {formatearCRC(p.totalCostoLcy)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KpisGrid({ kpi }: { kpi: KpiObra }) {
  const items = [
    {
      label: 'Avance ponderado',
      value: `${(kpi.pctAvancePonderado * 100).toFixed(1)}%`,
      tone: 'default' as const,
    },
    {
      label: '% Presupuesto consumido',
      value: `${(kpi.pctPresupuestoConsumido * 100).toFixed(1)}%`,
      tone: kpi.pctPresupuestoConsumido > 1 ? 'danger' : 'default',
    },
    {
      label: 'Costo total',
      value: formatearCRC(kpi.costoTotal),
    },
    {
      label: 'Presupuesto total',
      value: formatearCRC(kpi.presupuestoCostoTotal),
    },
    {
      label: 'Costo directo',
      value: formatearCRC(kpi.costoDirecto),
    },
    {
      label: 'Presup. directo',
      value: formatearCRC(kpi.presupuestoCostoDirecto),
    },
    {
      label: 'CPI directo',
      value: kpi.cpiDirecto.toFixed(2),
      tone: kpi.cpiDirecto < 1 ? 'danger' : 'success',
    },
    {
      label: 'Costo/m²',
      value: formatearCRC(kpi.costoRealXM2),
    },
    {
      label: 'Presup./m²',
      value: formatearCRC(kpi.presupuestoXM2),
    },
    {
      label: '% Desviación/m²',
      value: `${(kpi.pctDesviacionXM2 * 100).toFixed(1)}%`,
      tone: kpi.pctDesviacionXM2 > 0 ? 'danger' : 'success',
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">KPIs · {kpi.fechaCalculo}</CardTitle>
        {kpi.alertaSobrecosto && (
          <Badge
            variant={
              kpi.alertaSobrecosto.toLowerCase() === 'rojo'
                ? 'destructive'
                : kpi.alertaSobrecosto.toLowerCase() === 'amarillo'
                  ? 'secondary'
                  : 'default'
            }
            className="text-[10px]"
          >
            {kpi.alertaSobrecosto}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3 pt-2">
        {items.map((it) => (
          <div key={it.label} className="space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {it.label}
            </div>
            <div
              className={`text-base font-semibold tabular-nums ${
                it.tone === 'danger'
                  ? 'text-red-600'
                  : it.tone === 'success'
                    ? 'text-emerald-600'
                    : ''
              }`}
            >
              {it.value}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function Sparkline({ puntos }: { puntos: PuntoAvanceMensual[] }) {
  if (puntos.length === 0) return null;

  const W = 600;
  const H = 60;
  const maxPct = 1;
  const minPct = 0;
  const ancho = puntos.length > 1 ? W / (puntos.length - 1) : 0;

  const path = puntos
    .map((p, i) => {
      const x = i * ancho;
      const y = H - ((p.pctAvance - minPct) / (maxPct - minPct)) * H;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-16 mb-2" role="img">
      <title>Avance físico mensual</title>
      <line x1={0} y1={H} x2={W} y2={H} stroke="#e4e4e7" strokeWidth={1} />
      <line x1={0} y1={0} x2={W} y2={0} stroke="#e4e4e7" strokeWidth={1} />
      <path d={path} fill="none" stroke="#16a34a" strokeWidth={2} />
      {puntos.map((p, i) => {
        const x = i * ancho;
        const y = H - p.pctAvance * H;
        return (
          <circle key={`${p.anio}-${p.mes}`} cx={x} cy={y} r={2} fill="#16a34a">
            <title>{`${NOMBRES_MES[p.mes - 1]} ${p.anio}: ${(p.pctAvance * 100).toFixed(1)}%`}</title>
          </circle>
        );
      })}
    </svg>
  );
}

function TablaAvance({ puntos }: { puntos: PuntoAvanceMensual[] }) {
  if (puntos.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[10px]">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="px-2 py-1">Mes</th>
            <th className="px-2 py-1 text-right">% Avance</th>
          </tr>
        </thead>
        <tbody>
          {puntos.map((p) => (
            <tr key={`${p.anio}-${p.mes}`} className="border-t border-zinc-100">
              <td className="px-2 py-1">
                {NOMBRES_MES[p.mes - 1]} {p.anio}
              </td>
              <td className="px-2 py-1 text-right tabular-nums">
                {(p.pctAvance * 100).toFixed(1)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
